import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSettings } from "@/context/SettingsContext";

/**
 * SEO component — sets title, meta, Open Graph, Twitter cards and JSON-LD schema.
 * Also strips any duplicate meta description injected by the hosting platform
 * so only the react-helmet-managed tag remains.
 */
export default function Seo({
  title,
  description,
  keywords,
  image,
  path = "",
  type = "website",
  schema,
}) {
  const { settings } = useSettings();
  const site = settings?.company_name || "Legacy India Travel";
  const defaults = settings?.default_seo || {};

  const fullTitle = title ? `${title}` : defaults.meta_title || site;
  const desc = description || defaults.meta_description || "";
  const kw = keywords || defaults.keywords || "";
  const ogImage = image || defaults.og_image || "";
  const url = `${window.location.origin}${path}`;

  // Deduplicate meta[name=description] injected by the hosting platform.
  // react-helmet-async does not tag its client-side inserts with data-rh,
  // so we dedupe by content instead: keep exactly one tag with the page description.
  useEffect(() => {
    if (!desc) return;
    const dedupe = () => {
      const tags = Array.from(document.querySelectorAll('meta[name="description"]'));
      if (tags.length === 0) {
        const m = document.createElement("meta");
        m.setAttribute("name", "description");
        m.setAttribute("content", desc);
        document.head.appendChild(m);
        return;
      }
      // Prefer the tag that already has the right content; else the last one (helmet writes last).
      const preferred = tags.find((t) => t.getAttribute("content") === desc) || tags[tags.length - 1];
      preferred.setAttribute("content", desc);
      tags.forEach((t) => { if (t !== preferred) t.remove(); });
    };
    dedupe();
    const t1 = setTimeout(dedupe, 150);
    const t2 = setTimeout(dedupe, 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [desc]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {kw && <meta name="keywords" content={kw} />}
      <link rel="canonical" href={url} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={site} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      {ogImage && <meta property="og:image" content={ogImage} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
}
